import { KNOWN_MAKES, isKnownMake } from './knownMakes';
import { editDistance } from './compare';
import type { VerdictComponent, VerdictField } from './types';

export type JunkFlag = 'make-in-wrong-field' | 'make-model-concatenated' | 'note-not-value' | 'multiple-values' | 'make-misspelled';

const key = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
const MAKE_KEYS = Object.keys(KNOWN_MAKES);
/** A "Make - Model" string is short; anything longer is not scanned for one (keeps every pattern below linear). */
const MAX_CONCAT_LEN = 128;
/** The longest make key worth an edit-distance spell check; the longest real make key is 13. */
const MAX_MAKE_KEY_LEN = 32;

/** "John Deere - 4045HF285": a known make, a spaced dash, then something. Length-capped, split rather than a regex. */
function isMakeModelConcatenated(v: string): boolean {
  if (v.length > MAX_CONCAT_LEN) return false;
  const [head, ...rest] = v.split(/\s+-\s+/);
  return rest.join('').trim() !== '' && /^[A-Za-z][A-Za-z .]*$/.test(head) && isKnownMake(key(head));
}

function isMisspelledMake(k: string): boolean {
  if (k.length > MAX_MAKE_KEY_LEN || isKnownMake(k)) return false;
  return MAKE_KEYS.some((m) => m.length >= 4 && editDistance(k, m, 2) <= 2);
}

/** Placeholder text typed instead of a value ("N/A", "none", "TBD"): a note, never "more than one value" because of its slash. */
const PLACEHOLDER_KEYS: ReadonlySet<string> = new Set(['NA', 'NONE', 'UNKNOWN', 'UNK', 'TBD', 'NULL']);

export function junkFlags(_component: VerdictComponent, field: VerdictField, raw: string | null): JunkFlag[] {
  const v = (raw ?? '').trim();
  if (!v) return [];
  const k = key(v);
  if (PLACEHOLDER_KEYS.has(k)) return ['note-not-value'];
  const flags: JunkFlag[] = [];
  if (field !== 'make' && isKnownMake(k)) flags.push('make-in-wrong-field');
  if (field !== 'make' && isMakeModelConcatenated(v)) flags.push('make-model-concatenated');
  const words = v.split(/\s+/).filter((w) => /^[A-Za-z]{3,}$/.test(w));
  if (field !== 'make' && words.length >= 3) flags.push('note-not-value');
  if (field !== 'make' && /[/,;]|\bSPEC\b/i.test(v) && v.split(/[/,;]/).filter((p) => p.trim()).length > 1) flags.push('multiple-values');
  if (field === 'make' && isMisspelledMake(k)) flags.push('make-misspelled');
  return flags;
}
